import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Frame, Toast, useBreakpoints } from '@shopify/polaris';
import { MenuIcon } from '@shopify/polaris-icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAxiosInstance } from '../../context/AxiosContext';
import { usePromotions, CARDS, BANNERS } from './api';
import Sidebar from './Sidebar';
import * as style from '../layout/index.module.scss';
import * as sidebarStyles from '../layout/Sidebar.module.scss';
import { useAdminSignOut } from '../settings/api';
import RateLimitToast from '../common/RateLimitToast';
import logo from '../../../assets/starapps-light-bg.svg';

const ToastContext = createContext(() => { });
export const useToast = () => useContext(ToastContext);

// Bootstrapped with the page, so the card editor can offer a partner picker instead of an id box.
const ReferrersContext = createContext([]);
export const useReferrers = () => useContext(ReferrersContext);

const Layout = ({ email, referrers }) => {
  const axiosInstance = useAxiosInstance();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const { smDown } = useBreakpoints();
  const [toast, setToast] = useState(null);

  const { data: cards } = usePromotions({ resource: CARDS, axiosInstance });
  const { data: banners } = usePromotions({ resource: BANNERS, axiosInstance });

  const { mutate: signOut } = useAdminSignOut({
    onSuccess: () => { window.location.href = '/referrers'; },
    onError: () => { window.location.href = '/referrers'; },
  });

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  useEffect(() => { setNavOpen(false); }, [pathname]);

  const counts = {
    cards: cards ? cards.length : undefined,
    live: banners ? banners.filter((b) => b.status === 'live').length : undefined,
  };

  return (
    <ToastContext.Provider value={setToast}>
      <ReferrersContext.Provider value={referrers || []}>
        <div className={sidebarStyles.container}>
          <header className={style.header}>
            <div className={style.imageContainer}>
              {smDown && (
                <Button
                  icon={MenuIcon}
                  variant="tertiary"
                  accessibilityLabel="Open menu"
                  onClick={() => setNavOpen(true)}
                />
              )}
              <img src={logo} alt="StarApps" className={style.images} />
              {/* Amber 50/300/700, written out because this repo has no Tailwind. */}
              <span
                style={{
                  border: '1px solid #FCD34D', background: '#FFFBEB', color: '#B45309',
                  fontSize: 11, fontWeight: 500, lineHeight: 1.4, padding: '2px 8px',
                  borderRadius: 6, letterSpacing: '.02em', whiteSpace: 'nowrap',
                }}
              >
                Admin
              </span>
            </div>

            <div className={style.marginForLogout} style={{ position: 'relative' }}>
              <button
                type="button"
                aria-label="Account menu"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                className={style.activator}
                style={{ border: 0, padding: 0 }}
                onClick={() => setMenuOpen((open) => !open)}
              >
                {(email || '?').charAt(0).toUpperCase()}
              </button>

              {menuOpen && (
                <>
                  <button
                    type="button"
                    aria-hidden="true"
                    tabIndex={-1}
                    onClick={() => setMenuOpen(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 900, border: 0, background: 'transparent', cursor: 'default' }}
                  />
                  <div
                    role="menu"
                    style={{
                      position: 'absolute', top: 48, right: 0, zIndex: 901, minWidth: 220,
                      background: 'var(--p-color-bg-surface, #fff)',
                      border: '1px solid var(--p-color-border, #e3e3e3)',
                      borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,.14)', overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        padding: '10px 14px', borderBottom: '1px solid var(--p-color-border, #e3e3e3)',
                        fontSize: 12, color: 'var(--p-color-text-secondary, #616161)', wordBreak: 'break-all',
                      }}
                    >
                      {email || 'Signed in'}
                    </div>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); signOut(); }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px',
                        border: 0, background: 'transparent', font: 'inherit', fontSize: 13,
                        cursor: 'pointer', color: 'var(--p-color-text, #303030)',
                      }}
                    >
                      Log out
                    </button>
                  </div>
                </>
              )}
            </div>
          </header>

          <Frame
            navigation={<Sidebar pathname={pathname} onNavigate={navigate} counts={counts} />}
            showMobileNavigation={navOpen}
            onNavigationDismiss={() => setNavOpen(false)}
          >
            <Outlet />
            {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
            <RateLimitToast />
          </Frame>
        </div>
      </ReferrersContext.Provider>
    </ToastContext.Provider>
  );
};

Layout.propTypes = {
  email: PropTypes.string,
  referrers: PropTypes.array,
};

export default Layout;
